'use client';

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { GoogleMap, InfoWindow, useJsApiLoader } from '@react-google-maps/api';

import RichText from '@/components/ui/RichText/RichText';

import { parseLatLng } from '@/components/layout/LocationSection/helpers';
import { MapMarker } from '@/components/layout/LocationSection/types';

type Props = {
  markers?: MapMarker[];
  zoomLevel?: number;
};

const containerStyle = {
  width: '100%',
  height: '100%',
};

const mapOptions: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  mapId: process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID?.trim() || 'DEMO_MAP_ID',
};

const googleMapsLibraries: ['marker'] = ['marker'];

const SinglePointMap: React.FC<Props> = ({ markers = [], zoomLevel = 14 }) => {
  const [activeMarker, setActiveMarker] = useState<number | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);

  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY?.trim() || '';
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
    libraries: googleMapsLibraries,
  });

  const validMarkers = useMemo(() => {
    return markers
      .map((marker, index) => {
        const position = parseLatLng(marker?.text);
        if (!position) return null;
        return { index, marker, position };
      })
      .filter(Boolean) as Array<{
      index: number;
      marker: MapMarker;
      position: { lat: number; lng: number };
    }>;
  }, [markers]);

  const mapCenter = validMarkers[0]?.position;

  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;

      markersRef.current.forEach((m) => (m.map = null));
      markersRef.current = [];

      validMarkers.forEach(({ position, index }) => {
        const advancedMarker = new google.maps.marker.AdvancedMarkerElement({
          map,
          position,
        });

        advancedMarker.addListener('gmp-click', () => {
          setActiveMarker(index);
        });

        markersRef.current.push(advancedMarker);
      });
    },
    [validMarkers],
  );

  const onMapUnmount = useCallback(() => {
    markersRef.current.forEach((m) => (m.map = null));
    markersRef.current = [];
    mapRef.current = null;
  }, []);

  if (!isLoaded || !mapCenter) return null;

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      options={mapOptions}
      center={mapCenter}
      zoom={zoomLevel}
      onLoad={onMapLoad}
      onUnmount={onMapUnmount}
    >
      {activeMarker !== null && validMarkers[activeMarker] && (
        <InfoWindow
          position={validMarkers[activeMarker].position}
          onCloseClick={() => setActiveMarker(null)}
        >
          <div>
            {validMarkers[activeMarker].marker?.heading ? (
              <RichText className="[&>p]:font-inherit text-primary-300 text-lg font-bold [&>p]:mb-0 [&>p]:text-inherit">
                {validMarkers[activeMarker].marker.heading.content}
              </RichText>
            ) : (
              validMarkers[activeMarker].marker?.title || null
            )}
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  );
};

export default SinglePointMap;
